import { ApiCarData } from '@interfaces/car-api.types';
import { getBrowseCardConfigs } from '@factories/browseCardFactory';
import { parseSlugToFilters } from '@helpers/navigationHelpers';
import type { SlugFilters } from '@utils/slugGenerator';
import { hydrateFilterCache } from '@utils/filtersCached';
import type { ApiFilterResponse } from '@interfaces/filter.types';

export interface CarWithBrowseCard {
  type: 'car' | 'browse';
  data: ApiCarData | ReturnType<typeof getBrowseCardConfigs>[0];
  id: string;
}

export interface ServerBrowseCardConfig {
  type: 'brand' | 'bodyType' | 'fuelType' | 'transmission' | 'seatingCapacity' | 'budget' | 'feature';
  title: string;
  options: Array<{
    label: string;
    value: string;
    icon?: string;
  }>;
  showLimited?: boolean;
  limitCount?: number;
  parentTitle?: string;
}

// Cache for BrowseCard configs to avoid repeated calculations
const browseCardConfigCache = new Map<string, ReturnType<typeof getBrowseCardConfigs>>();

export function generateBrowseCards(
  cars: ApiCarData[],
  filterOptions: any,
  currentSlugFilters: SlugFilters,
  isMobile: boolean = false,
): CarWithBrowseCard[] {
  if (!cars || cars.length === 0) return [];

  const selectedFilterCount = Object.values(currentSlugFilters || {}).reduce(
    (count, value) => {
      if (Array.isArray(value)) {
        return value.length > 0 ? count + 1 : count;
      }
      return value ? count + 1 : count;
    },
    0
  );

  if (selectedFilterCount >= 3) {
    return cars.map((car, index) => ({
      type: 'car' as const,
      data: car,
      id: `car-${index}`,
    }));
  }

  // Create cache key for filter options
  const cacheKey = JSON.stringify(filterOptions);

  // Use cached configs if available
  let browseCardConfigs: ReturnType<typeof getBrowseCardConfigs>;
  if (browseCardConfigCache.has(cacheKey)) {
    browseCardConfigs = browseCardConfigCache.get(cacheKey)!;
  } else {
    browseCardConfigs = getBrowseCardConfigs(filterOptions);
    browseCardConfigCache.set(cacheKey, browseCardConfigs);
  }

  // Early return if no BrowseCard configs
  if (!browseCardConfigs || browseCardConfigs.length === 0) {
    return cars.map((car, index) => ({
      type: 'car' as const,
      data: car,
      id: `car-${index}`,
    }));
  }

  // Filter BrowseCards based on current slug filters
  const filteredBrowseCards = browseCardConfigs.filter(config => {
    if (!currentSlugFilters || Object.keys(currentSlugFilters).length === 0) {
      return true;
    }

    const filterType = config.type;
    switch (filterType) {
      case 'brand':
        return !currentSlugFilters.brand;
      case 'bodyType':
        return !currentSlugFilters.bodyType;
      case 'fuelType':
        return !currentSlugFilters.fuelType;
      case 'transmission':
        return !currentSlugFilters.transmission;
      case 'seatingCapacity':
        return !currentSlugFilters.seatingCapacity;
      case 'budget':
        return !currentSlugFilters.budget;
      default:
        return true;
    }
  });

  // Early return if no filtered BrowseCards
  if (filteredBrowseCards.length === 0) {
    return cars.map((car, index) => ({
      type: 'car' as const,
      data: car,
      id: `car-${index}`,
    }));
  }

  // Pre-calculate intervals for better performance
  const insertInterval = isMobile ? 4 : 6;
  const result: CarWithBrowseCard[] = [];
  let browseCardIndex = 0;

  // Use a more efficient loop with pre-allocated array
  for (let i = 0; i < cars.length; i++) {
    const car = cars[i];

    // Add car
    result.push({
      type: 'car',
      data: car,
      id: `car-${i}`,
    });

    // Add BrowseCard at intervals if available
    if ((i + 1) % insertInterval === 0 && browseCardIndex < filteredBrowseCards.length) {
      const browseCardConfig = filteredBrowseCards[browseCardIndex];
      result.push({
        type: 'browse',
        data: browseCardConfig,
        id: `browse-${browseCardIndex}`,
      });
      browseCardIndex++;
    }
  }

  return result;
}

// Cache for parsed slug filters
const slugFilterCache = new Map<string, SlugFilters>();

export function getCurrentSlugFiltersServer(
  pathname: string,
  filterOptions?: ApiFilterResponse | null
): SlugFilters {
  hydrateFilterCache(filterOptions);

  const decodedPathname = decodeURIComponent(pathname);
  if (slugFilterCache.has(decodedPathname)) {
    return slugFilterCache.get(decodedPathname)!;
  }

  const pathParts = decodedPathname.split('/');
  let currentSlug = 'all-cars';

  if (pathParts && pathParts.length >= 3 && pathParts[1] === 'collection') {
    currentSlug = pathParts[2];
  }
  const result = parseSlugToFilters(currentSlug);

  if (Object.keys(result).length > 0) {
    slugFilterCache.set(decodedPathname, result);
  }

  return result;
}

// Function to clear caches (useful for testing or memory management)
export function clearBrowseCardCaches(): void {
  browseCardConfigCache.clear();
  slugFilterCache.clear();
}
